<template>
  <div class="style-manage-page">
    <header class="page-hero">
      <div>
        <span class="eyebrow">Style Center</span>
        <h2>风格管理</h2>
        <p>维护前台可用风格，支持新增、筛选、预览与编辑。</p>
      </div>
      <div class="hero-stats">
        <div>
          <strong>{{ styleList.length }}</strong>
          <span>当前风格</span>
        </div>
        <div>
          <strong>{{ recommendCount }}</strong>
          <span>推荐展示</span>
        </div>
      </div>
    </header>

    <div class="manage-layout">
      <section class="editor-panel">
        <div class="panel-head">
          <div>
            <h3>{{ isEditMode ? '编辑风格' : '新增风格' }}</h3>
            <p>{{ isEditMode ? '修改后会覆盖当前风格资料' : '填写风格资料并上传预览图' }}</p>
          </div>
          <el-tag v-if="isEditMode" effect="dark" type="warning">编辑中</el-tag>
        </div>

        <el-form ref="styleFormRef" :model="styleForm" :rules="formRules" label-position="top" class="style-form">
          <div class="form-grid">
            <el-form-item label="风格名称" prop="name">
              <el-input v-model="styleForm.name" placeholder="例如：3D东方奇幻" maxlength="100" show-word-limit />
            </el-form-item>

            <el-form-item label="分类目录" prop="dirname">
              <el-input v-model="styleForm.dirname" placeholder="例如：国风、写实、卡通" maxlength="50" show-word-limit />
            </el-form-item>
          </div>

          <el-form-item label="是否推荐" prop="isRecommend">
            <el-segmented v-model="styleForm.isRecommend" :options="recommendOptions" class="recommend-segment" />
          </el-form-item>

          <el-form-item label="风格提示词（中文）" prop="prompt">
            <el-input v-model="styleForm.prompt" type="textarea" placeholder="请输入风格提示词，建议描述画风、材质、光线、色彩与镜头质感" :rows="8"
              maxlength="800" show-word-limit resize="none" />
          </el-form-item>

          <el-form-item label="风格提示词（英文）" prop="enprompt">
            <el-input v-model="styleForm.enprompt" type="textarea" placeholder="请输入风格提示词，建议描述画风、材质、光线、色彩与镜头质感" :rows="8"
              maxlength="800" show-word-limit resize="none" />
          </el-form-item>

          <el-form-item label="风格图片" prop="imgbs64">
            <el-upload class="style-uploader" action="#" :show-file-list="false" :auto-upload="false"
              :on-change="handleImageChange" :before-upload="beforeUpload" accept="image/jpeg,image/png,image/webp">
              <div v-if="imageUrl" class="image-preview">
                <img :src="imageUrl" alt="风格图片预览" />
                <button type="button" class="remove-image" @click.stop="removeImage">移除图片</button>
              </div>
              <div v-else class="upload-placeholder">
                <el-icon>
                  <Plus />
                </el-icon>
                <b>上传风格图片</b>
                <span>JPG / PNG / WEBP，10MB以内</span>
              </div>
            </el-upload>
          </el-form-item>

          <div class="form-actions">
            <el-button class="ghost-btn" @click="resetForm">{{ isEditMode ? '取消编辑' : '重置' }}</el-button>
            <el-button type="primary" class="submit-btn" :loading="isSubmitting" @click="submitForm">
              {{ isEditMode ? '保存修改' : '提交保存' }}
            </el-button>
          </div>
        </el-form>
      </section>

      <section class="list-panel">
        <div class="panel-head list-head">
          <div>
            <h3>风格列表</h3>
            <p>点击编辑可回填到左侧表单</p>
          </div>
          <div class="toolbar">
            <el-input v-model="searchDirname" clearable placeholder="按分类目录筛选" @input="getStyleList">
              <template #prefix>
                <el-icon>
                  <Search />
                </el-icon>
              </template>
            </el-input>
            <el-button class="ghost-btn" :loading="isLoadingList" @click="getStyleList">
              <el-icon>
                <Refresh />
              </el-icon>
              刷新
            </el-button>
          </div>
        </div>

        <div v-if="isLoadingList" class="loading-box">
          <el-skeleton :rows="5" animated />
        </div>

        <el-empty v-else-if="!styleList.length" description="暂无风格数据" />

        <div v-else class="style-grid">
          <article v-for="item in styleList" :key="item.key || `${item.dirname}-${item.label}`" class="style-card">
            <el-image class="style-cover" :src="item.cover" :preview-src-list="[item.cover]" fit="cover"
              preview-teleported>
              <template #error>
                <div class="image-fallback">无图</div>
              </template>
            </el-image>
            <div class="style-info">
              <div class="style-title">
                <h4>{{ item.label }}</h4>
                <el-tag size="small" :type="item.isRecommend ? 'warning' : 'info'" effect="dark">
                  {{ item.isRecommend ? '推荐' : '普通' }}
                </el-tag>
              </div>
              <div class="meta-row">
                <span>{{ item.dirname || '未分类' }}</span>
                <span>{{ formatTime(item.createTime) }}</span>
              </div>
              <p>{{ item.prompt }}</p>
              <div class="card-actions">
                <el-button class="ghost-btn" size="small" @click="startEdit(item)">编辑</el-button>
                <el-button class="danger-btn" size="small" @click="handleDelete(item)">删除</el-button>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Refresh, Search } from '@element-plus/icons-vue'
import axios from 'axios'
import { API_URL } from '@/util/urls'

const styleFormRef = ref(null)
const isSubmitting = ref(false)
const imageUrl = ref('')
const editingStyle = ref(null)

const styleList = ref([])
const isLoadingList = ref(false)
const searchDirname = ref('')

const recommendOptions = [
  { label: '普通', value: '0' },
  { label: '推荐', value: '1' }
]

const styleForm = reactive({
  name: '',
  prompt: '',
  enprompt: '',
  isRecommend: '0',
  dirname: '',
  imgbs64: ''
})

const isEditMode = computed(() => Boolean(editingStyle.value))
const recommendCount = computed(() => styleList.value.filter(item => item.isRecommend).length)

const formRules = reactive({
  name: [{ required: true, message: '请输入风格名称', trigger: 'blur' }],
  prompt: [{ required: true, message: '请输入风格提示词', trigger: 'blur' }],
  enprompt: [{ required: true, message: '请输入英文风格提示词', trigger: 'blur' }],
  dirname: [
    { required: true, message: '请输入分类目录', trigger: 'blur' },
    { pattern: /^[\u4e00-\u9fa5A-Za-z0-9_-]+$/, message: '仅支持中文、字母、数字、下划线和短横线', trigger: 'blur' }
  ],
  isRecommend: [{ required: true, message: '请选择是否推荐', trigger: 'change' }],
  imgbs64: [
    {
      validator: (rule, value, callback) => {
        if (!isEditMode.value && !value) {
          callback(new Error('请上传风格图片'))
          return
        }
        callback()
      },
      trigger: 'change'
    }
  ]
})

onMounted(() => {
  getStyleList()
})

const submitForm = async () => {
  try {
    await styleFormRef.value.validate()
    isSubmitting.value = true

    const submitData = {
      name: styleForm.name.trim(),
      prompt: styleForm.prompt.trim(),
      enprompt: styleForm.enprompt.trim(),
      isRecommend: Number(styleForm.isRecommend),
      dirname: styleForm.dirname.trim(),
      imgbs64: styleForm.imgbs64
    }

    const res = isEditMode.value
      ? await axios.post('/admin/updateStyle', {
        ...submitData,
        id: editingStyle.value?.id || editingStyle.value?.key,
        oldName: editingStyle.value?.label,
        oldDirname: editingStyle.value?.dirname,
        oldImgName: editingStyle.value?.imgName
      })
      : await axios.post('/api/addStyle', submitData)

    if (res.data?.msg === 'success') {
      ElMessage.success(isEditMode.value ? '风格修改成功' : '风格添加成功')
      resetForm()
      getStyleList()
    } else {
      ElMessage.error(res.data?.message || res.data?.msg || '保存失败')
    }
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || '提交失败，请稍后重试'
    ElMessage.error(message)
  } finally {
    isSubmitting.value = false
  }
}

const resetForm = () => {
  editingStyle.value = null
  styleFormRef.value?.resetFields()
  Object.assign(styleForm, {
    name: '',
    prompt: '',
    enprompt: '',
    isRecommend: '0',
    dirname: '',
    imgbs64: ''
  })
  imageUrl.value = ''
}

const startEdit = (row) => {
  editingStyle.value = row
  Object.assign(styleForm, {
    name: row.label || '',
    prompt: row.prompt || '',
    enprompt: row.enprompt || '',
    isRecommend: row.isRecommend ? '1' : '0',
    dirname: row.dirname || '',
    imgbs64: ''
  })
  imageUrl.value = row.cover || ''
  styleFormRef.value?.clearValidate()
}

const beforeUpload = (file) => {
  const isLt10M = file.size / 1024 / 1024 < 10
  if (!isLt10M) {
    ElMessage.error('图片大小不能超过10MB')
    return false
  }

  const isImg = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type)
  if (!isImg) {
    ElMessage.error('仅支持 JPG / PNG / WEBP 格式')
    return false
  }

  return true
}

const handleImageChange = (file) => {
  if (!file?.raw || beforeUpload(file.raw) === false) return
  const reader = new FileReader()
  reader.onload = (event) => {
    styleForm.imgbs64 = event.target.result
    imageUrl.value = event.target.result
    styleFormRef.value?.validateField('imgbs64')
  }
  reader.readAsDataURL(file.raw)
}

const removeImage = () => {
  imageUrl.value = ''
  styleForm.imgbs64 = ''
  styleFormRef.value?.validateField('imgbs64')
}

const getStyleList = async () => {
  try {
    isLoadingList.value = true
    const params = {
      dirname: searchDirname.value || 'all'
    }
    const res = await axios.get('/api/getStyleList', { params })
    if (res.data?.msg === 'success') {
      styleList.value = (res.data.data || []).map(normalizeStyleItem)
    } else {
      ElMessage.error(res.data?.message || '获取列表失败')
    }
  } catch (error) {
    ElMessage.error(error?.response?.data?.message || '获取列表失败，请稍后重试')
  } finally {
    isLoadingList.value = false
  }
}

const normalizeStyleItem = (item) => {
  const imgUrl = item.imgUrl || `/images/style/${item.dirname}/${item.imgName}`
  return {
    id: item.id,
    key: item.id,
    label: item.name,
    cover: `${API_URL}/api/getfile?filename=${imgUrl}`,
    prompt: item.prompt,
    enprompt: item.enprompt || '',
    dirname: item.dirname,
    imgName: item.imgName,
    imgUrl,
    isRecommend: Number(item.tag) === 1,
    createTime: item.createTime
  }
}

const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(`确定要删除风格「${row.label}」吗？`, '删除确认', {
      confirmButtonText: '确定删除',
      cancelButtonText: '取消',
      type: 'warning'
    })

    const res = await axios.post('/api/deleteStyle', {
      name: row.label,
      imgName: row.imgName,
      dirname: row.dirname
    })

    if (res.data?.msg === 'success') {
      ElMessage.success('删除成功')
      if (editingStyle.value?.key === row.key) resetForm()
      getStyleList()
    } else {
      ElMessage.error(res.data?.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error?.response?.data?.message || '删除失败，请稍后重试')
    }
  }
}

const formatTime = (time) => {
  if (!time) return '无时间'
  try {
    return new Date(time).toLocaleString()
  } catch (error) {
    return '无时间'
  }
}
</script>

<style lang="scss" scoped>
.style-manage-page {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  padding: 24px 24px 10px 24px;
  background: #070b12;
  color: #e8edf7;
  display: flex;
  flex-direction: column;
}

:global(.el-main:has(.style-manage-page)) {
  height: calc(100vh - 60px);
  padding: 0;
  overflow: hidden;
  background: #070b12;
}

.page-hero {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-end;
  padding: 24px;
  border: 1px solid #202938;
  border-radius: 10px;
  background: linear-gradient(135deg, #111827 0%, #0b111d 58%, #120f18 100%);
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.28);
  margin-bottom: 18px;

  h2 {
    margin: 6px 0 8px;
    font-size: 28px;
    line-height: 1.15;
    color: #ffffff;
  }

  p {
    margin: 0;
    color: #8f9bad;
  }
}

.eyebrow {
  color: #ff9b4a;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.hero-stats {
  display: flex;
  gap: 12px;

  div {
    width: 112px;
    padding: 14px;
    border: 1px solid #263142;
    border-radius: 8px;
    background: rgba(10, 15, 24, 0.76);
  }

  strong,
  span {
    display: block;
  }

  strong {
    color: #ffffff;
    font-size: 24px;
    margin-bottom: 4px;
  }

  span {
    color: #8f9bad;
    font-size: 12px;
  }
}

.manage-layout {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  grid-template-columns: minmax(360px, 430px) minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.editor-panel,
.list-panel {
  border: 1px solid #202938;
  border-radius: 10px;
  background: #0d131f;
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.24);
}

.editor-panel {
  position: sticky;
  top: 20px;
  padding: 20px;
  max-height: calc(100vh - 260px);
  overflow-y: auto;
  scrollbar-gutter: stable;
  box-sizing: border-box;
}

.editor-panel::-webkit-scrollbar {
  width: 8px;
}

.editor-panel::-webkit-scrollbar-track {
  background: #0a101b;
  border-radius: 999px;
}

.editor-panel::-webkit-scrollbar-thumb {
  background: #2b374b;
  border-radius: 999px;
}

.editor-panel::-webkit-scrollbar-thumb:hover {
  background: #3b4a62;
}

.list-panel {
  padding: 18px;
  min-width: 0;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;

  h3 {
    margin: 0 0 6px;
    color: #f4f7fb;
    font-size: 18px;
  }

  p {
    margin: 0;
    color: #7f8a9c;
    font-size: 13px;
  }
}

.list-head {
  align-items: center;
}

.toolbar {
  display: flex;
  gap: 10px;
  align-items: center;

  .el-input {
    width: 230px;
  }
}

.style-form {
  :deep(.el-form-item__label) {
    color: #cbd5e1;
    font-weight: 600;
  }
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.recommend-segment {
  width: 100%;
  --el-segmented-bg-color: #151d2b;
  --el-segmented-item-selected-bg-color: rgba(255, 116, 23, 0.18);
  --el-segmented-item-selected-color: #ff9b4a;
  --el-segmented-item-hover-bg-color: rgba(255, 255, 255, 0.05);
}

.style-uploader {
  width: 100%;

  :deep(.el-upload) {
    width: 100%;
  }
}

.image-preview,
.upload-placeholder {
  width: 100%;
  height: 220px;
  border: 1px dashed #324058;
  border-radius: 10px;
  background: #101827;
  overflow: hidden;
}

.image-preview {
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.remove-image {
  position: absolute;
  right: 12px;
  bottom: 12px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.68);
  color: #ffffff;
  padding: 7px 12px;
  cursor: pointer;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: #99a6ba;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;

  &:hover {
    border-color: #ff7417;
    background: #141d2c;
  }

  .el-icon {
    font-size: 30px;
    color: #ff9b4a;
  }

  b {
    color: #e8edf7;
  }

  span {
    font-size: 12px;
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 6px;
}

.ghost-btn,
.danger-btn,
.submit-btn {
  border-radius: 6px;
}

.ghost-btn {
  border-color: #314059;
  background: #121a28;
  color: #d6deea;

  &:hover {
    border-color: #ff7417;
    color: #ff9b4a;
    background: #161f2f;
  }
}

.danger-btn {
  border-color: rgba(248, 113, 113, 0.38);
  background: rgba(127, 29, 29, 0.18);
  color: #fca5a5;

  &:hover {
    border-color: #ef4444;
    color: #ffffff;
    background: rgba(185, 28, 28, 0.42);
  }
}

.loading-box {
  padding: 16px;
}

.style-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}

.style-card {
  display: flex;
  gap: 12px;
  min-width: 0;
  padding: 12px;
  border: 1px solid #202938;
  border-radius: 10px;
  background: #101827;
  transition: transform 0.18s, border-color 0.18s, background 0.18s;

  &:hover {
    transform: translateY(-2px);
    border-color: #35445d;
    background: #121c2b;
  }
}

.style-cover {
  width: 104px;
  height: 132px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #172033;
}

.image-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #788397;
}

.style-info {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.style-title {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;

  h4 {
    margin: 0;
    color: #f5f7fb;
    font-size: 15px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.meta-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin: 8px 0;
  color: #7f8a9c;
  font-size: 12px;
}

.style-info p {
  flex: 1;
  margin: 0 0 12px;
  color: #aab4c4;
  font-size: 13px;
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

:deep(.el-input__wrapper),
:deep(.el-textarea__inner) {
  background: #121a28;
  border: 1px solid #2b374b;
  box-shadow: none;
  color: #e8edf7;
}

:deep(.el-input__wrapper:hover),
:deep(.el-textarea__inner:hover),
:deep(.el-input__wrapper.is-focus),
:deep(.el-textarea__inner:focus) {
  border-color: #ff7417;
}

:deep(.el-input__inner),
:deep(.el-textarea__inner) {
  color: #e8edf7;
}

:deep(.el-input__count),
:deep(.el-input__count-inner),
:deep(.el-textarea .el-input__count),
:deep(.el-textarea .el-input__count-inner) {
  background: transparent !important;
  color: #667286;
}

:deep(.el-empty__description p) {
  color: #8f9bad;
}

@media (max-width: 1180px) {
  .manage-layout {
    grid-template-columns: 1fr;
  }

  .editor-panel {
    position: static;
    max-height: none;
    overflow: visible;
  }
}
</style>
